import type { FC } from "react"

import { UNIT_OF_MEASURE } from "@logic/lib/powur-os"
import { useRecoilState, useRecoilValue } from "recoil"

import useUpdateExtendedWarranty from "~/api/hooks/useUpdateExtendedWarranty"
import {
  designLocalState,
  designRemoteState,
} from "~/containers/Proposal/state/designs/design-state"
import { withExtendedWarrantySnapshot } from "~/containers/Proposal/state/designs/extended-warranty-snapshot"
import { useDispatchError } from "~/containers/Proposal/state/notifications/QueryErrorModal"
import { proposalRemoteState } from "~/containers/Proposal/state/proposal/proposal-state"
import { useGetWarrantyPricingQuery } from "~/graphql/client"
import { useGraphqlClient } from "~/hooks/graphql"

import { Card, CardsContainer } from "./styles"

const Warranties: FC<{ disableCards: boolean }> = ({ disableCards }) => {
  const [local, setLocal] = useRecoilState(designLocalState)
  const [remote, setRemote] = useRecoilState(designRemoteState)
  const proposal = useRecoilValue(proposalRemoteState)
  const { client } = useGraphqlClient()
  const onError = useDispatchError()

  const {
    isLoading,
    mutateAsync,
    variables: pendingWarrantyId,
  } = useUpdateExtendedWarranty(proposal.id, remote.id)

  const getWarrantyPricing = useGetWarrantyPricingQuery(client, {
    proposalId: proposal.id,
  })

  const isSelected = (warrantyId: number): boolean =>
    remote.extendedWarrantyPowurOsId === warrantyId

  const toggleWarranty = async (warrantyId: number): Promise<void> => {
    try {
      const snapshot = await mutateAsync(
        isSelected(warrantyId) ? null : warrantyId,
      )

      const applySelection = (current: typeof remote): typeof remote =>
        withExtendedWarrantySnapshot(current, snapshot)

      setLocal(applySelection)
      setRemote(applySelection)
    } catch (error) {
      onError(error)
    }
  }

  return (
    <CardsContainer aria-busy={isLoading}>
      <div
        role="status"
        className={`flex min-h-[44px] w-full items-center gap-3 rounded-lg border px-4 py-3 font-sans text-sm font-medium text-foreground ${
          isLoading ? `border-border bg-surface` : `border-transparent`
        }`}
      >
        {isLoading ? (
          <>
            <span
              aria-hidden="true"
              className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"
            />
            {pendingWarrantyId === null
              ? `Removing warranty...`
              : `Saving warranty...`}
          </>
        ) : null}
      </div>
      {getWarrantyPricing.data?.getWarrantyPricing.map(
        ({ id, cost, eligible, name }) => {
          const selected = isSelected(id)

          return (
            <Card
              allowDisabledToggle={
                selected && !eligible && !disableCards && !isLoading
              }
              key={id}
              id={`` + id}
              disabled={!eligible || disableCards || isLoading}
              title={name}
              toggle={() => {
                void toggleWarranty(id)
              }}
              unit={UNIT_OF_MEASURE.PROJECT}
              selected={selected}
              cost={cost}
            />
          )
        },
      )}
    </CardsContainer>
  )
}

export default Warranties
